import { useEffect, useMemo } from "react";
import { useImageStream } from "../hooks/useImageStream";
import { useAccess } from "../hooks/useAccess";
import { getStandingNudge } from "../config/moderationPresentation";
import { pickImageErrorContext, formatCategoryDetailsLine } from "../config/imageProviderErrorPresentation";
import ImageLoader from "./ImageLoader";
import ModerationAddendum from "./ModerationAddendum";
import "../App.css";

// Both image-moderation codes the backend can send — a scene-prompt or
// output block ("moderation_output_blocked") or an unparseable provider
// refusal ("moderation"). "moderation_blocked" is deliberately absent here:
// it's the text pipeline's own code (battleResult.js) for the user's
// literal typed input, which the image pipeline never emits. Used only to
// decide whether to refetch Account Standing and show the standing
// addendum — the actual title/detail copy is driven by presentationKey
// (config/imageProviderErrorPresentation.js), not this list.
const MODERATION_ERROR_CODES = ["moderation_output_blocked", "moderation"];

// aria-live copy for the 3 meaningful status transitions only — never for a
// partial-image refinement, which fires far too often to announce.
function liveStatusMessage(displayStatus) {
  if (displayStatus === "loading") return "Generating battle scene image…";
  if (displayStatus === "ready") return "Battle scene image ready.";
  if (displayStatus === "error") return "Battle scene image generation stopped.";
  return "";
}

export default function BattleImage({ prompt, imageKey, size = "1024x1024", model = "gpt-image-2" }) {
  const {
    status, url, partialSrc, partialIndex,
    errCode, presentationKey, previewEligible, categoryDetails,
    regenerating,
    regenErrCode, regenPresentationKey, regenPreviewEligible, regenCategoryDetails,
    regenerate,
  } = useImageStream({ prompt, imageKey, size, model });
  const { standing, retry: retryStanding } = useAccess();

  // A moderation block can escalate the account's standing server-side as a
  // side effect of the same request — refetch so the addendum below reflects
  // current standing, not whatever was cached before this block happened.
  // Safe against repeated identical blocks: useImageStream resets errCode/
  // regenErrCode at the start of every attempt, so this dependency
  // genuinely changes each time.
  useEffect(() => {
    if (MODERATION_ERROR_CODES.includes(errCode) || MODERATION_ERROR_CODES.includes(regenErrCode)) {
      retryStanding();
    }
  }, [errCode, regenErrCode, retryStanding]);

  const addendum = getStandingNudge(standing);

  const displayStatus = regenerating ? "loading" : status;
  const liveMessage = useMemo(() => liveStatusMessage(displayStatus), [displayStatus]);

  if (!prompt || !imageKey) return null;

  const showDegradedPreview = displayStatus === "error" && previewEligible && partialSrc;
  const errorContext = pickImageErrorContext(presentationKey, { previewEligible });
  const categoryLine = formatCategoryDetailsLine(categoryDetails);

  // A failed regeneration that left a prior canonical image in place shows a
  // compact, non-destructive notice alongside that image instead of an error
  // panel — the failure reason is never fully suppressed. `url` being
  // truthy here means the current successfully-displayed image predates
  // this particular regen attempt (see useImageStream's hadCanonical logic).
  const hasRegenNotice = status === "ready" && Boolean(url) && Boolean(regenErrCode);
  const regenNoticeContext = hasRegenNotice
    ? pickImageErrorContext(regenPresentationKey, { hasCanonical: true, previewEligible: regenPreviewEligible })
    : null;
  const regenNoticeCategoryLine = hasRegenNotice ? formatCategoryDetailsLine(regenCategoryDetails) : null;

  // Regeneration is reachable from any known state (a valid image or an
  // already-determined error, including a blocked one) — the backend
  // already supports retrying from a blocked baseline, so this is only
  // about surfacing that existing capability, never a new client-side
  // eligibility rule. Auth/tier/policy/standing/throttle/quota stay
  // exclusively backend-enforced regardless of which state the button is
  // clicked from.
  const canRegenerate = status === "ready" || status === "error";

  return (
    <div className="battle-image-wrap">
      <div aria-live="polite" className="visually-hidden">{liveMessage}</div>
      {displayStatus === "loading" && (
        <>
          {partialSrc ? (
            <div className="partial-image-wrap">
              <img src={partialSrc} alt="Generating battle scene..." className="partial-image-preview" />
              <div className="partial-image-badge">
                <span className="partial-image-dot" />
                Generating… {partialIndex + 1}/4
              </div>
            </div>
          ) : (
            <ImageLoader
              src=""
              alt="Loading battle scene"
              width="100%"
              height="100%"
              gridSize={18}
              cellGap={4}
              cellShape="square"
              cellColor="#1a3a3a"
              blinkSpeed={1400}
              loadingDelay={60000}
            />
          )}
          <p className="image-disclaimer">
            AI-generated images may not accurately depict copyrighted characters.
          </p>
        </>
      )}
      {displayStatus === "error" && (
        showDegradedPreview ? (
          <div className="partial-image-wrap">
            <img src={partialSrc} alt="Latest generated preview" className="partial-image-preview" />
            <div className="partial-image-badge">
              <span className="partial-image-dot" />
              {errorContext.detail}
            </div>
          </div>
        ) : (
          <div className="image-error">
            <strong>{errorContext.title}</strong>
            <div className="image-error-detail">{errorContext.detail}</div>
            {categoryLine && <span className="image-error-category">{categoryLine}</span>}
            <ModerationAddendum addendum={MODERATION_ERROR_CODES.includes(errCode) ? addendum : null} as="div" />
          </div>
        )
      )}
      {displayStatus === "ready" && url && (
        <ImageLoader
          src={url}
          alt="AI battle scene"
          width="100%"
          height="100%"
          gridSize={18}
          cellGap={4}
          cellShape="square"
          cellColor="#1a3a3a"
          blinkSpeed={1400}
          transitionDuration={800}
          fadeOutDuration={600}
          loadingDelay={0}
        />
      )}
      {canRegenerate && (
        <div className="regen-row">
          <button
            className="regen-btn"
            onClick={regenerate}
            disabled={regenerating}
            title="Generate a new battle image"
          >
            {regenerating ? "Regenerating…" : "↻ Regenerate Image"}
          </button>
          {hasRegenNotice && (
            <span className="regen-notice">
              {regenNoticeContext.title}. {regenNoticeContext.detail}
              {regenNoticeCategoryLine && <span className="regen-notice-category">{regenNoticeCategoryLine}</span>}
              <ModerationAddendum addendum={MODERATION_ERROR_CODES.includes(regenErrCode) ? addendum : null} />
            </span>
          )}
        </div>
      )}
    </div>
  );
}
